<template>
  <div class="relative bg-surface p-6 text-main" data-testid="visitor-contact-form">
    <img src="@/assets/images/profiles/visitor-profile-form-decoration.svg" class="absolute -top-17.5 -left-13 size-40" />

    <h2 class="text-center text-headline-xs">{{ $t('VisitorProfiles.contactForm.headline') }}</h2>

    <div class="mt-6">
      <TextInput
        v-model="formData.name"
        :label="$t('VisitorProfiles.contactForm.name')"
        :error="getValidationErrors(v$.name)"
        data-testid="visitor-contact-name"
      />
      <TextInput
        v-model="formData.email"
        :label="$t('VisitorProfiles.contactForm.email')"
        :error="getValidationErrors(v$.email)"
        data-testid="visitor-contact-email"
      />
      <TextInput
        v-model="formData.company"
        :label="$t('VisitorProfiles.contactForm.company')"
        :error="getValidationErrors(v$.company)"
        data-testid="visitor-contact-company"
      />
      <TextInput
        v-model="formData.message"
        :label="$t('VisitorProfiles.contactForm.message')"
        type="textarea"
        placeholder="Write your message here..."
        :error="getValidationErrors(v$.message)"
        data-testid="visitor-contact-message"
      />
    </div>

    <button
      class="btn-primary-color-s mt-4 w-full"
      :disabled="v$.$dirty && v$.$invalid"
      data-testid="visitor-contact-send"
      @click="sendMessage"
    >
      {{ $t('VisitorProfiles.contactForm.send') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { useVuelidate } from '@vuelidate/core'
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import { useSendVisitorProfileMessageMutation, ContactTransactionTypeEnum } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, email } from '@/services/validators'
import { useMeStore } from '@/stores'

import type { VisitorProfile } from './types'
const props = defineProps<{
  visitor: VisitorProfile
}>()

const meStore = useMeStore()
const { t } = useI18n()

const { mutate: sendVisitorProfileMessage, onDone, onError } = useSendVisitorProfileMessageMutation()

const formData = reactive({
  name: meStore.userName,
  email: meStore.userEmail,
  company: meStore.me?.partnerCompany?.displayName ?? meStore.me?.partnerCompany?.name ?? '',
  message: '',
})

const rules = {
  name: { required },
  email: { required, email },
  company: { required },
  message: { required },
}

const v$ = useVuelidate(rules, formData)

async function sendMessage() {
  const validForm = await v$.value.$validate()
  if (!validForm) return

  const result = await sendVisitorProfileMessage({
    visitorProfileId: props.visitor.id,
    senderName: formData.name,
    senderEmail: formData.email,
    senderCompany: formData.company,
    message: formData.message,
    contactTransactionType: ContactTransactionTypeEnum.VisitorProfile,
  })

  if (result?.data?.createContactTransaction?.errors?.length) {
    result.data.createContactTransaction.errors.forEach((error) => {
      showError(error)
    })
  }
}

onDone(() => {
  showNotification(t('VisitorProfiles.contactForm.messageSent'), 'success')
  formData.message = ''
  v$.value.$reset()
})

onError((error) => {
  showError(error.message)
})

function showError(error: string) {
  if (error) showNotification(error, 'error')
}
</script>
